import * as React from 'react'
import { Loader2 } from 'lucide-react'
import { cn } from '../../lib/utils'

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'solid' | 'outline' | 'ghost'
  size?: 'xs' | 'sm' | 'md' | 'lg'
  color?: 'dark' | 'blue' | 'red' | 'gray'
  loading?: boolean
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(({
  className,
  variant = 'solid',
  size = 'md',
  color = 'dark',
  loading,
  disabled,
  children,
  ...props
}, ref) => {
  const sizeClasses = {
    xs: 'px-2 py-0.5 text-[11px] gap-1',
    sm: 'px-3 py-1.5 text-xs gap-1.5',
    md: 'px-4 py-2 text-sm gap-2',
    lg: 'px-5 py-2.5 text-sm gap-2',
  }

  const solidColors = {
    dark: 'border border-gray-900 bg-gray-900 text-white hover:bg-gray-800 hover:border-gray-800',
    blue: 'border border-transparent bg-blue-600 text-white hover:bg-blue-700',
    red: 'border border-transparent bg-red-600 text-white hover:bg-red-700',
    gray: 'border border-transparent bg-gray-100 text-gray-700 hover:bg-gray-200',
  }

  const outlineColors = {
    dark: 'border border-gray-300 text-gray-700 hover:bg-gray-50',
    blue: 'border border-blue-300 text-blue-600 hover:bg-blue-50',
    red: 'border border-red-300 text-red-600 hover:bg-red-50',
    gray: 'border border-gray-200 text-gray-600 hover:bg-gray-50',
  }

  const ghostColors = {
    dark: 'text-gray-700 hover:bg-gray-100',
    blue: 'text-blue-600 hover:bg-blue-50',
    red: 'text-red-600 hover:bg-red-50',
    gray: 'text-gray-500 hover:bg-gray-100',
  }

  const variantClasses = {
    solid: solidColors[color],
    outline: outlineColors[color],
    ghost: ghostColors[color],
  }

  return (
    <button
      ref={ref}
      disabled={disabled || loading}
      className={cn(
        'inline-flex items-center justify-center font-medium rounded-full transition-colors cursor-pointer',
        'disabled:opacity-50 disabled:cursor-not-allowed',
        sizeClasses[size],
        variantClasses[variant],
        loading && 'opacity-80',
        className,
      )}
      {...props}
    >
      {loading && <Loader2 size={size === 'sm' ? 14 : 16} className="animate-spin" />}
      {children}
    </button>
  )
})
Button.displayName = 'Button'

export interface IconButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  size?: 'xs' | 'sm' | 'md'
  variant?: 'ghost' | 'outline'
}

export const IconButton = ({ className, size = 'sm', variant = 'ghost', children, ...props }: IconButtonProps) => {
  const sizeClasses = {
    xs: 'p-0.5',
    sm: 'p-1',
    md: 'p-1.5',
  }

  const variantClasses = {
    ghost: 'hover:bg-gray-100 text-gray-500 hover:text-gray-700',
    outline: 'border border-gray-200 bg-white/90 hover:bg-gray-50 text-gray-500 hover:text-gray-700 shadow-xs',
  }

  return (
    <button
      className={cn(
        'inline-flex items-center justify-center rounded-full transition-colors cursor-pointer',
        sizeClasses[size],
        variantClasses[variant],
        className
      )}
      {...props}
    >
      {children}
    </button>
  )
}
